/**
 * TransferDocumentDialog
 * 
 * Confirmation dialog for transferring a document lock from one conversation to another.
 * Used when a user tries to edit a document that's already locked by a different conversation.
 */

import { useState } from 'react';
import type { DocumentEditLock } from '../contexts/AIContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  Button,
  Spinner,
} from '@/components/ui';

interface TransferDocumentDialogProps {
  /** The lock on the document that would be transferred */
  existingLock: DocumentEditLock;
  /** The conversation that wants to take over editing */
  targetConversationId: string;
  targetConversationTitle: string;
  /** Called when transfer is confirmed */
  onConfirm: () => void;
  /** Called when transfer is cancelled */
  onCancel: () => void;
  open?: boolean;
}

export default function TransferDocumentDialog({
  existingLock,
  targetConversationId: _targetConversationId,
  targetConversationTitle,
  onConfirm,
  onCancel,
  open = true,
}: TransferDocumentDialogProps) {
  const [isTransferring, setIsTransferring] = useState(false);

  const handleConfirm = async () => {
    setIsTransferring(true);
    try {
      onConfirm();
    } finally {
      setIsTransferring(false);
    }
  };

  // Lock color for visual indicator
  const lockColorClass = {
    'blue': 'bg-info',
    'green': 'bg-success-light0',
    'purple': 'bg-secondary0',
    'orange': 'bg-warning',
    'pink': 'bg-secondary',
    'cyan': 'bg-info',
  }[existingLock.colorCode] || 'bg-muted0';

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onCancel()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className={`w-3 h-3 rounded-full ${lockColorClass}`} />
            <DialogTitle>Transfer Document Editing</DialogTitle>
          </div>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <p className="text-sm text-muted-foreground">
            The document <strong className="text-foreground">{existingLock.documentId}</strong> is 
            currently being edited in:
          </p>
          
          <div className="p-3 bg-muted rounded-lg border">
            <div className="flex items-center gap-2">
              <span className={`w-2 h-2 rounded-full ${lockColorClass}`} />
              <span className="font-medium">
                {existingLock.lockedByConversationTitle}
              </span>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Locked at {new Date(existingLock.lockedAt).toLocaleTimeString()}
            </p>
          </div>

          <p className="text-sm text-muted-foreground">
            Transfer editing to <strong className="text-foreground">{targetConversationTitle}</strong>?
          </p>

          <div className="p-3 bg-warning-light rounded-lg border border-warning">
            <p className="text-xs text-warning">
              ⚠️ The other conversation will no longer be able to edit this document until you release it or transfer it back.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="secondary"
            onClick={onCancel}
            disabled={isTransferring}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={isTransferring}
          >
            {isTransferring && <Spinner size="sm" className="mr-2" />}
            {isTransferring ? 'Transferring...' : 'Transfer'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
